import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library

// Define the structure of a toast message
interface ToastMessage {
  id: string;
  text: string;
  isError: boolean; // New field to differentiate error messages
}

// Define the context value
interface ToastContextValue {
  addToast: (message: string, isError?: boolean) => void;
}

// Create the context
const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Custom hook to use the Toast context
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Props for the ToastProvider component
interface ToastProviderProps {
  children: ReactNode;
}

// ToastProvider component
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  // Add a message to the queue
  const addToast = useCallback((message: string, isError: boolean = false) => {
    setMessages((prev) => [...prev, { id: uuidv4(), text: message, isError }]);
  }, []);

  // Remove a message by id
  const removeToast = useCallback((id: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  // Automatically remove the first message after 5 seconds
  useEffect(() => {
    if (messages.length > 0) {
      const timer = setTimeout(() => {
        removeToast(messages[0].id);
      }, 2500);

      return () => clearTimeout(timer); // Cleanup timer
    }
  }, [messages, removeToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div style={{ position: 'fixed', bottom: 20, right: 20 }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              background: '#333333EE',
              color: '#fff',
              padding: '10px 20px',
              marginBottom: '10px',
              borderRadius: '5px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
              border: msg.isError ? '2px solid red' : '2px solid transparent', // Highlight errors
            }}
          >
            <span>{msg.text}</span>
            <button
              onClick={() => removeToast(msg.id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '16px',
                cursor: 'pointer',
                marginLeft: '10px',
              }}
            >
              X
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
